/**
 * The navigation model, as data.
 *
 * S-D3 approved a sidebar with two modules and eight-ish sections each; almost
 * none of those sections has a screen yet, and the sessions that build them are
 * named beside each entry below. Two ways to handle that were available and
 * only one is honest:
 *
 *  * **Hide what does not exist.** The sidebar then grows silently over a dozen
 *    sessions and is never laid out at its real size until the last one lands —
 *    and the approved information architecture is invisible to anyone reviewing
 *    the shell.
 *  * **Render it disabled.** The model is visible and testable at full size,
 *    and "not available yet" is a state, announced in text (`aria-disabled`
 *    plus a visible label) rather than by colour.
 *
 * The second. What makes it not a placeholder is that there is nothing left to
 * wire up here: the session that builds a module adds its route and flips
 * `to`, and this file is the only edit the shell needs.
 */

export type ModuleId = 'outreach' | 'seo'

export interface NavSection {
  label: string
  /** Absent while the module that owns this section has not shipped. */
  to?: string
  /** The session that brings it — for the reader of this file, not the user. */
  arrivesIn: string
}

export interface ModuleDefinition {
  id: ModuleId
  label: string
  /** Where the switcher lands when this module is chosen. */
  home: string
  sections: NavSection[]
}

export const MODULES: readonly ModuleDefinition[] = [
  {
    id: 'outreach',
    label: 'Outreach',
    home: '/outreach',
    sections: [
      { label: 'Overview', to: '/outreach', arrivesIn: 'S-B8' },
      { label: 'Leads', arrivesIn: 'S-B13' },
      { label: 'Inbox', arrivesIn: 'S-B19' },
      { label: 'Pipeline', arrivesIn: 'S-B20' },
      { label: 'Niche packs', arrivesIn: 'S-B21' },
      { label: 'Suppression', arrivesIn: 'S-B12' },
      { label: 'Settings', to: '/settings/organisation', arrivesIn: 'S-B8' },
    ],
  },
  {
    id: 'seo',
    label: 'SEO',
    home: '/seo',
    sections: [
      { label: 'Overview', to: '/seo', arrivesIn: 'S-B8' },
      { label: 'Sites', arrivesIn: 'S-B22' },
      { label: 'Keywords', arrivesIn: 'S-B24' },
      { label: 'Technical audit', arrivesIn: 'S-B23' },
      { label: 'Content', arrivesIn: 'S-B25' },
      { label: 'Approvals', arrivesIn: 'S-B27' },
      { label: 'Reasoning trail', arrivesIn: 'S-B26' },
      { label: 'Settings', to: '/settings/organisation', arrivesIn: 'S-B8' },
    ],
  },
]

/** Where the shell remembers which module you were last in. */
export const MODULE_STORAGE_KEY = 'augrank.module'

export function moduleById(id: ModuleId): ModuleDefinition {
  const found = MODULES.find((module) => module.id === id)
  // Total by construction — `ModuleId` is the union of the ids above — but a
  // non-null assertion here would be the one place a typo in that union became
  // a blank sidebar instead of an error.
  if (!found) throw new Error(`No module definition for "${id}"`)
  return found
}

/**
 * Which module a path belongs to.
 *
 * `/settings` is shared: it is one organisation's settings whichever module you
 * reached it from, so it does not decide the module and the caller's remembered
 * choice stands. Returning null rather than defaulting is what lets the shell
 * keep the switcher where the user left it while they are in settings.
 */
export function moduleForPath(pathname: string): ModuleId | null {
  if (pathname === '/outreach' || pathname.startsWith('/outreach/')) return 'outreach'
  if (pathname === '/seo' || pathname.startsWith('/seo/')) return 'seo'
  return null
}

/**
 * The remembered module, falling back to SEO.
 *
 * SEO rather than Outreach because it is the module the product leads with and
 * the one a trial starts in (REQUIREMENTS §6). Any stored value that is not a
 * module id is ignored — `localStorage` is user-writable, and a typo there
 * should not be able to produce a shell with no navigation.
 */
export function rememberedModule(storage: Pick<Storage, 'getItem'>): ModuleId {
  const stored = storage.getItem(MODULE_STORAGE_KEY)
  return stored === 'outreach' || stored === 'seo' ? stored : 'seo'
}
